import { createApi } from '@reduxjs/toolkit/query/react';
import { User, UserWithCompany, UserWithToken } from '@typeRoots/common/user';
import { setCurrentUser, setUser, setProducerPhoneNumber } from '../slice/userSlice';
import { baseQueryWithProgress } from 'src/helpers/fetchQueryHelper';
import { PRODUCER_PHONE_NUMBER } from 'src/Constants';

export const userApi = createApi({
  reducerPath: 'userApi',

  baseQuery: baseQueryWithProgress,

  tagTypes: ['User'], // Cache invalidation by these tags

  endpoints: (builder) => ({
    //send jwt to get user data (deprecated, use authenticationApi instead)
    verifyUserWithJwt2: builder.query<UserWithCompany, string>({
      query: (jwt) => ({
        url: `/auth/verify-jwt?token=${jwt}`, // Fetch user data
        method: 'POST',
        headers: {
          Authorization: jwt, // Add the JWT token to the Authorization header
        },
      }),

      async onQueryStarted(_params, { dispatch, queryFulfilled }) {
        try {
          const { data: userInfo } = await queryFulfilled;
          const { user } = userInfo;
          dispatch(setUser(user)); // add this user in users array
          dispatch(setCurrentUser(user)); // set this user as current user
        } catch (error) {
          console.log('Error getting users from jwt', error);
        }
      },
      // Tagging vehicles to automatically refresh this cache when needed
      providesTags: ['User'],
    }),
    getProducerPhone: builder.query<{ phone: string }, void>({
      query: () => 'users/producer/phone',
      async onQueryStarted(_data, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled; // data: { phone: string }
          const phone = data.phone ?? PRODUCER_PHONE_NUMBER;
          dispatch(setProducerPhoneNumber(phone));
        } catch {
          console.log('Failed to get producer phone number');
        }
      },
    }),
    // Get all users
    getUsers: builder.query<User[], void>({
      query: () => '/users',
      providesTags: ['User'], // This endpoint provides 'User' tag, which can be used for cache invalidation
    }),

    // Get a user by ID
    getUserById: builder.query<User, number>({
      query: (id) => `/users/${id}`,
      providesTags: (result, error, id) => [{ type: 'User', id }],
    }),

    // Create a new user
    createUser: builder.mutation<UserWithToken, { newUser: Partial<User>; token: string }>({
      query: ({ newUser, token }) => ({
        url: 'auth/register',
        method: 'POST',
        body: { newUser, token },
      }),
      async onQueryStarted(_params, { dispatch, queryFulfilled }) {
        try {
          const { data: userData } = await queryFulfilled;
          dispatch(setUser(userData.user)); // add this user in users array
          dispatch(setCurrentUser(userData.user)); // set this user as current user
        } catch (error) {
          console.log(_params, '  :::: ');
          console.log('Error creating users', error);
        }
      },
    }),

    // Update an existing user by ID
    updateUser: builder.mutation<User, { id: number; user: Partial<User> }>({
      query: ({ id, user }) => ({
        url: `/users/${id}`,
        method: 'PATCH',
        body: user,
      }),
      // Invalidate the cache for this specific user so the data is refreshed when it's updated
      invalidatesTags: ['User'],
    }),

    // Delete a user by ID
    deleteUser: builder.mutation<void, number>({
      query: (id) => ({
        url: `/users/${id}`,
        method: 'DELETE',
      }),
      // Invalidate the cache for the deleted user and refetch the list
      invalidatesTags: (result, error, id) => [{ type: 'User', id }],
    }),
  }),
});

// Export the hooks for usage in components
export const {
  useGetUsersQuery, // For fetching users when the component mounts
  useLazyGetUsersQuery, // For fetching users conditionally (e.g., on button click)
  useGetUserByIdQuery, // For fetching a single user by ID
  useCreateUserMutation, // For creating a new user
  useUpdateUserMutation, // For updating an existing user
  useDeleteUserMutation, // For deleting a user
  useVerifyUserWithJwt2Query,
  useLazyVerifyUserWithJwt2Query,
  useGetProducerPhoneQuery,
  useLazyGetProducerPhoneQuery,
} = userApi;
